import React from 'react';
import LegalShell from './legal/LegalShell';

const H2 = ({ children }) => (
  <h2 className="text-xl font-extrabold text-white mt-8 mb-2" style={{ color: '#00D26A' }}>
    {children}
  </h2>
);
const Rule = ({ kind = 'do', children }) => (
  <div className="flex gap-3 items-start">
    <span
      className="inline-flex items-center justify-center w-6 h-6 rounded-md text-black font-bold text-sm flex-shrink-0 mt-0.5"
      style={{ background: kind === 'do' ? '#00D26A' : '#E04A3F' }}
    >
      {kind === 'do' ? '✓' : '✕'}
    </span>
    <div>{children}</div>
  </div>
);

export default function CommunityGuidelines() {
  return (
    <LegalShell title="Community Guidelines" lastUpdated="20 April 2026" version="v1 DRAFT">
      <p>
        Flipping Friends works because of the people on it. These guidelines keep the community
        safe, honest, and useful for resellers of every size. <strong>Break them and you&apos;ll
        lose access to the platform — no warnings for the serious ones.</strong>
      </p>

      <H2>The Core Rules</H2>
      <div className="space-y-4 bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <Rule kind="do">
          <p><strong className="text-white">Keep transactions on eBay, Vinted or other regulated marketplaces.</strong> Flipping Friends is informational only. You get buyer protection, feedback and refund rights from the marketplace — not from us.</p>
        </Rule>
        <Rule kind="dont">
          <p><strong className="text-white">No contact-detail sharing in match messages.</strong> Do not ask for or share phone numbers, personal email addresses, WhatsApp, social media handles or payment details through Flipping Friends messaging. Violations are grounds for immediate removal.</p>
        </Rule>
        <Rule kind="dont">
          <p><strong className="text-white">No off-platform payments.</strong> Never pay or accept payment through bank transfer, cash-on-collection, PayPal Friends &amp; Family, or any other off-marketplace channel initiated through Flipping Friends.</p>
        </Rule>
        <Rule kind="do">
          <p><strong className="text-white">Submit honest prices.</strong> Only submit prices you have personally verified from the buyer&apos;s official website on the date shown. Inaccurate submissions pollute the database for every user.</p>
        </Rule>
        <Rule kind="do">
          <p><strong className="text-white">Respect the sacrifice protocol on batch checks.</strong> Submit the sacrifice barcodes first, confirm the expected totals, and only then submit the remainder. Batches that skip this step will be forfeit.</p>
        </Rule>
        <Rule kind="dont">
          <p><strong className="text-white">No harassment, hate speech, or discrimination.</strong> Don&apos;t target anyone for their race, religion, gender, sexual orientation, disability or any other protected characteristic. Keep disagreements civil.</p>
        </Rule>
        <Rule kind="dont">
          <p><strong className="text-white">No impersonation or fake usernames.</strong> Don&apos;t pretend to be Flipping Friends staff, a buyer platform, or another user. Admin / support / official usernames are reserved.</p>
        </Rule>
        <Rule kind="dont">
          <p><strong className="text-white">No scraping, bots, or bulk automation.</strong> One human, one account. Scripted submissions, proxy networks or automated price scraping will end your account.</p>
        </Rule>
        <Rule kind="do">
          <p><strong className="text-white">Report issues fairly.</strong> If a seller misrepresents an item on eBay / Vinted, raise it on that marketplace first. Only bring it to us if a Flipping Friends rule (e.g. contact sharing) was also broken.</p>
        </Rule>
        <Rule kind="do">
          <p><strong className="text-white">Keep your wanted-list notes relevant.</strong> Notes are for item context (edition, condition) — not contact details, not advertising, not personal messages.</p>
        </Rule>
      </div>

      <H2>How we enforce</H2>
      <p>
        We may, at our discretion, <strong>warn</strong>, <strong>mute</strong>,{' '}
        <strong>remove matches or wants</strong>, <strong>forfeit earnings</strong>,{' '}
        <strong>suspend</strong> or <strong>permanently ban</strong> any account that breaks these
        rules. Serious breaches — fraud, threats, contact-sharing, bots — result in an immediate
        permanent ban and forfeiture of any outstanding balance.
      </p>
      <p>
        If you believe an action was taken in error, reply to the enforcement email and we&apos;ll
        review. Appeals are considered once per incident.
      </p>

      <H2>Reporting</H2>
      <p>
        Saw something that breaks these rules? Please email{' '}
        <a href="mailto:marcus@flippingfriends.co.uk" className="text-[#00D26A] underline">
          marcus@flippingfriends.co.uk
        </a>{' '}
        with the match ID or username in question and a brief description. All reports are
        confidential. Good-faith reports are welcome; malicious false reports are themselves a
        violation.
      </p>

      <H2>Changes</H2>
      <p>
        We may update these guidelines as the community grows and new behaviours emerge.
        Material changes will be announced on the website. Continued use after an update means
        you accept the new version.
      </p>
    </LegalShell>
  );
}
